<script lang="ts" setup>
import type { Addon, AddonGroup } from '~/models';

import AddonItem from './AddonItem.vue';

import type { AccordionItem } from '#ui/types';

const props = defineProps<{
  id: string;
  group: AddonGroup;
  productPrice: number;
}>();

const emits = defineEmits<{
  (e: 'addAddon', group: AddonGroup, addon: Addon): void;
  (e: 'removeAddon', group: AddonGroup, addon: Addon): void;
}>();

const item = computed<AccordionItem>(() => ({
  defaultOpen: props.group.minChoices > 0
}));

const caption = computed<string>(() => {
  return props.group.minChoices > 0 || props.group.freeAmount > 0
    ? `Choose ${props.group.minChoices || props.group.freeAmount} options`
    : '';
});

const onAddAddon = (addon: Addon): void => {
  emits('addAddon', props.group, addon);
};

const onRemoveAddon = (addon: Addon): void => {
  emits('removeAddon', props.group, addon);
};
</script>

<template>
  <UAccordion
    v-if="props.group.visible"
    :items="[item]"
    :ui="{ item: { padding: '', size: 'text-base', color: 'text-inherit' } }"
  >
    <template #default>
      <div class="w-full">
        <UButton
          :id="props.id"
          class="
            w-full
            text-left
            border-t
            border-card
            text-base
            text-inherit
            bg-gray-100
            min-h-16
            disabled:opacity-100
            disabled:bg-gray-100
            focus-visible:ring-0
          "
          variant="ghost"
          :ui="{ rounded: '' }"
          data-test="item"
          :disabled="props.group.minChoices > 0"
        >
          <div class="flex-1 flex flex-col">
            <span
              class="text-xl font-roboto-bold"
              data-test="name"
            >
              {{ props.group.name }}
            </span>

            <span
              class="subtext-card text-sm font-semibold"
              data-test="caption"
            >
              {{ caption }}
            </span>
          </div>

          <template #trailing>
            <div
              v-if="props.group.minChoices > 0"
              class="text-xs bg-red-500 text-white px-1 py-0.5 rounded"
              data-test="required"
            >
              <span>Required</span>
            </div>

            <div
              v-else
              class="text-xs bg-white text-black px-1 py-0.5 rounded"
              data-test="optional"
            >
              <span>Optional</span>
            </div>
          </template>
        </UButton>
      </div>
    </template>

    <template #item>
      <ul>
        <AddonItem
          v-for="addon of props.group.addons"
          :key="addon.uuid"
          :addon="addon"
          :product-price="props.productPrice"
          :selection-type="props.group.maxChoices <= 1"
          data-test="addon"
          @add="() => onAddAddon(addon)"
          @remove="() => onRemoveAddon(addon)"
        />
      </ul>
    </template>
  </UAccordion>
</template>
